/**
 * Application Teaser Component
 * Displays only the image part of an application (featured image + logo overlay)
 * Used in block components like ApplicationsBlock on homepage
 */

"use client"

import type { ApplicationTeaser as ApplicationTeaserType } from "@/lib/drupal/get-applications"
import Image from "next/image"

interface ApplicationTeaserProps {
  /** Application data from GraphQL */
  application: ApplicationTeaserType
}

export function ApplicationTeaser({ application }: ApplicationTeaserProps) {
  const title = application.title || "Untitled application"
  const logoUrl = application.logo?.url || null
  const featuredUrl =
    application.featuredImage?.__typename === "MediaImage"
      ? application.featuredImage.mediaImage?.url
      : null
  const externalUrl = application.url?.url || null

  return (
    <div className="relative h-full">
      <div className="group flex flex-col h-full overflow-hidden bg-card/20 rounded-xs transition hover:shadow-md focus-within:ring-2 focus-within:ring-ring">
        <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
          {externalUrl ? (
            <a
              href={externalUrl}
              target="_blank"
              rel="noreferrer"
              className="block h-full w-full"
            >
              {featuredUrl ? (
                <Image
                  src={featuredUrl}
                  alt={title}
                  fill
                  className="object-cover transition-transform duration-300 group-hover:scale-[1.04]"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
                  No preview
                </div>
              )}
              <div className="absolute inset-0 flex items-end justify-start z-10 pointer-events-none">
                <div className="relative h-[60px] w-[120px] xl:h-[80px] xl:w-[160px] overflow-hidden rounded-r border bg-white/90 transition-transform duration-300 pointer-events-auto">
                  {logoUrl ? (
                    <Image
                      src={logoUrl}
                      alt={`${title} logo`}
                      fill
                      className="object-contain p-2"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                      {title ? title.charAt(0).toUpperCase() : "A"}
                    </div>
                  )}
                </div>
              </div>
            </a>
          ) : (
            <>
              {featuredUrl ? (
                <Image
                  src={featuredUrl}
                  alt={title}
                  fill
                  className="object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
                  No preview
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
